import type { BadgeTone } from "@/components/ui/Badge";
import { AP_INVOICE_DRAFT_VENDOR_NUMBER, type ApInvoiceRow, type ApInvoiceStatus } from "./apInvoicesApi";

export function apInvoiceWorkflowBadgeTone(status: ApInvoiceStatus): BadgeTone {
  switch (status) {
    case "draft":
      return "info";
    case "under_ai_review":
      return "warning";
    case "awaiting_human_review":
      return "warning";
    case "pending_tsh":
      return "warning";
    case "submitted":
      return "success";
    default:
      return "neutral";
  }
}

export function isApInvoiceEditable(status: ApInvoiceStatus): boolean {
  return status === "draft" || status === "awaiting_human_review";
}

export function apInvoiceListVendorInvoiceLabel(row: ApInvoiceRow): string {
  const raw = row.vendor_invoice_number;
  const hasVendorNumber =
    raw != null && raw.trim() !== "" && raw !== AP_INVOICE_DRAFT_VENDOR_NUMBER;
  if (hasVendorNumber) {
    return raw;
  }
  if (row.status === "draft" || row.status === "awaiting_human_review" || row.status === "under_ai_review") {
    return "Draft invoice";
  }
  return `Invoice #${row.id}`;
}

export function isApInvoicePendingTshWorkflow(status: ApInvoiceStatus): boolean {
  return status === "pending_tsh";
}

export function isApInvoiceSubmittedReview(status: ApInvoiceStatus): boolean {
  return status === "submitted";
}

/** Hide stage sublines that repeat the tier badge (for example mirrored GIPI). */
export function apInvoiceListShowsStageDetail(row: {
  tier_label: string;
  stage_label: string;
}): boolean {
  const stageLabel = row.stage_label.trim();
  if (
    stageLabel === ""
    || stageLabel === "Draft — not submitted"
    || stageLabel === "Invoice without receipt"
    || stageLabel === "Pending TSH"
    || stageLabel === "QuickBooks match confirmed"
  ) {
    return false;
  }
  if (
    row.tier_label === "GIPI in MV" &&
    stageLabel === "GIPI in MV"
  ) {
    return false;
  }
  return stageLabel !== row.tier_label;
}

export function isApInvoiceAiReviewPolling(status: ApInvoiceStatus): boolean {
  return status === "under_ai_review";
}

export function isApInvoiceDeletable(status: ApInvoiceStatus): boolean {
  return status === "draft" || status === "awaiting_human_review";
}

export function canResetApInvoiceToDraft(status: ApInvoiceStatus): boolean {
  return status === "awaiting_human_review" || status === "under_ai_review";
}

/** Keep the vendor invoice, but unlock edits, when Megaventory and QuickBooks do not have it yet. */
export function canReturnApInvoiceToDraftBeforeGipi(invoice: {
  status: ApInvoiceStatus;
  purchase_invoice_id: string | null;
  tsh_purchase_invoice_id: string | null;
  qbo_bill_id: string | null;
}): boolean {
  if (invoice.status !== "pending_tsh" && invoice.status !== "submitted") {
    return false;
  }
  const purchaseInvoiceId = (invoice.purchase_invoice_id ?? "").trim();
  const tshPurchaseInvoiceId = (invoice.tsh_purchase_invoice_id ?? "").trim();
  const qboBillId = (invoice.qbo_bill_id ?? "").trim();
  return purchaseInvoiceId === "" && tshPurchaseInvoiceId === "" && qboBillId === "";
}

export function canMigrateApInvoiceToManufacturer(
  status: ApInvoiceStatus,
  supplierId: string | null,
  supplierName: string | null,
  vendorInvoiceNumber: string | null,
  invoiceDate: string | null,
  manufacturerIntent = false,
): boolean {
  if (!isApInvoiceDeletable(status)) {
    return false;
  }
  const supplierDataId = supplierId == null ? null : Number.parseInt(supplierId, 10);
  const isSuntronicSupplier =
    (Number.isFinite(supplierDataId) && supplierDataId === 1734) ||
    /\bsuntronic\b/i.test((supplierName ?? "").trim());
  if (!isSuntronicSupplier && !manufacturerIntent) {
    return false;
  }
  const invoiceNumber = (vendorInvoiceNumber ?? "").trim();
  if (invoiceNumber === "" || invoiceNumber === "(draft)") {
    return false;
  }
  return (invoiceDate ?? "").trim() !== "";
}

export function canRetryApInvoiceAiReview(status: ApInvoiceStatus, hasStoredDocuments: boolean): boolean {
  if (status === "under_ai_review") {
    return true;
  }
  if (canResetApInvoiceToDraft(status)) {
    return true;
  }
  return status === "draft" && hasStoredDocuments;
}

export type ApInvoiceListQboActionInput = Pick<
  ApInvoiceRow,
  | "purchase_kind"
  | "status"
  | "tier"
  | "purchase_invoice_id"
  | "qbo_match_confirmed_at"
  | "qbo_bill_id"
  | "qbo_bill_sync_status"
>;

/** Hub-created QuickBooks bill for non-PO expense invoices. */
export function apInvoiceListCanCreateQboBill(row: ApInvoiceListQboActionInput): boolean {
  return (
    row.purchase_kind === "non_po_purchase" &&
    row.status === "submitted" &&
    (row.qbo_bill_id == null || row.qbo_bill_id.trim() === "") &&
    row.qbo_bill_sync_status !== "creating"
  );
}

/**
 * Manual "mark verified" — does not call QuickBooks. Requires a hub-linked GIPI for PO inventory,
 * or a submitted non-PO invoice after Create QuickBooks bill is no longer offered.
 */
export function apInvoiceListCanConfirmQboMatch(row: ApInvoiceListQboActionInput): boolean {
  if (row.qbo_match_confirmed_at != null) {
    return false;
  }
  if (apInvoiceListCanCreateQboBill(row)) {
    return false;
  }
  const purchaseInvoiceId = (row.purchase_invoice_id ?? "").trim();
  if (row.tier === 3 && purchaseInvoiceId !== "") {
    return true;
  }
  return row.purchase_kind === "non_po_purchase" && row.status === "submitted";
}

export function apInvoiceListConfirmQboMatchButtonLabel(isPending: boolean): string {
  return isPending ? "Saving…" : "Mark as verified in QBO";
}
